import { AccessFunction, AccessFunctionInterface, ConfiguredAccessFunction } from 'data/accessFunction/accessFunction.types';
import { CollectionFunction, CollectionFunctionInterface } from 'data/collectionFunction/collectionFunction.types';
import { SupportedFeaturesAccessFunctions, SupportedFeaturesCollectionFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import cloneDeep from 'lodash.clonedeep';
import _ from 'lodash';
import { Buffer } from 'buffer';
import { getEnabledAccessFunctions, getEnabledPoiAccessFunctions, getFilteredAccessFunctions } from 'data/accessFunction/accessFunction.adapter';
import { NodeData } from 'data/nodes/nodes.types';
import { RegisteredNode } from 'data/registeredNode/registeredNode.types';
import {
  NetworkTopologyElementsEnum,
  NetworkTopologyLocation,
  NetworkTopologyLocationCity,
  NetworkTopologyMapConfig,
  NetworkTopologyElement,
  NetworkTopologyStateEnum,
  NetworkTopologyFilter,
  NetworkTopologyFilterServerPayload,
  NetworkTopologyMapData,
  Connection,
  PoiXcipioNodeMap,
  CcpagDetail,
  AfConnection,
  AdmfLeader,
  AdmfStatus
} from './network-topology.types';
import NeGraph from './NeGraph';

const DEFAULT_US_CENTER_LATITUDE = 38;
const DEFAULT_US_CENTER_LONGITUDE = -98.5795;

export const getNetworkTopologyMapConfig = (zoom?: number, latitude?: number, longitude?: number): NetworkTopologyMapConfig => {
  return {
    facetLimit: 20000,
    mapPosition: {
      zoom: zoom || 5,
      maxZoom: 9,
      center: [
        latitude || DEFAULT_US_CENTER_LATITUDE,
        longitude || DEFAULT_US_CENTER_LONGITUDE
      ],
    },
    mapBaseLayer: {
      'Basemaps-Cartocdn': {
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        tileSize: 256,
        zoomOffset: 0
      },
    },
  };
}

const adaptXcipioNodeCaseFormat = (
  input: { element: NetworkTopologyElementsEnum, data: unknown[] }
): { element: NetworkTopologyElementsEnum, data: unknown[] } => {
  return {
    ...input,
    data: input.data.map((node: any) => ({
      id: node.id || node.id,
      nodeName: node.nodeName || node.NODENAME,
      nodeType: node.nodeType || node.NODETYPE,
      topology: node.topology || node.TOPOLOGY,
      ipAddress: node.ipAddress || node.IPADDR || node.IPADDR6,
      destNodeName: node.destNodeName || node.NODENAME,
      latitude: node.latitude || node.LATITUDE,
      longitude: node.longitude || node.LONGITUDE,
      country: node.country || node.COUNTRY,
      stateCode: node.statename || node.STATENAME,
      city: node.city || node.CITY,
      status: node.status
    }))
  }
}

const adaptDataCaseFormat = (input: { element: NetworkTopologyElementsEnum, data: unknown[] }[]) => {
  const clonedData = cloneDeep(input);

    return clonedData.map((item) => {
      if (item.element !== 'xcipio') {
        return item;
      }

      return adaptXcipioNodeCaseFormat(item);
    })
}

export function adaptFromServerFormat2(
  data: { element: NetworkTopologyElementsEnum, data: unknown[] }[],
  mapConfig?: NetworkTopologyMapConfig,
  ): NetworkTopologyMapData {
  return buildNetworkTopologyData(adaptDataCaseFormat(data), mapConfig);
}

export const buildCcpagDetailMap = (ccpagConnectionDetails: CcpagDetail[]) => {
  return {
    ccpagMdf3Map: buildCcpagToMdf3ConnectionMap(ccpagConnectionDetails),
    upfToCcpagMap: buildUpfToCcpagConnectionMap(ccpagConnectionDetails)
  };
}

export const buildCcpagToMdf3ConnectionMap = (ccpagConnectionDetails: CcpagDetail[]): { [key: string]: { id: string, status: string }[] } => {
  const ret: { [key: string]: { id: string, status: string }[] } = {};
  ccpagConnectionDetails.forEach((ccpag) => {
    ccpag.mdf3Connections.forEach((con) => {
      const ccpagId = Buffer.from(ccpag.id, 'base64').toString('binary') as string;
      const mdf3Id = Buffer.from(con.id, 'base64').toString('binary') as string;
      let status = '';
      if (!ret[ccpagId]) {
        ret[ccpagId] = [];
      }
      if (con.connectionStatuses && con.connectionStatuses.length > 0) {
        status = con.connectionStatuses[0].CONNSTATUS;
      }
      ret[ccpagId].push({
        id: mdf3Id,
        status
      });
    });
  });
  return ret;
}

export const buildUpfToCcpagConnectionMap = (ccpagConnectionDetails: CcpagDetail[]): { [key: string]: { id: string, status: string }[] } => {
  const ret: { [key: string]: { id: string, status: string }[] } = {};
  ccpagConnectionDetails.forEach((ccpag) => {
    ccpag.afConnections.forEach((con) => {
      if (!ret[con.id]) {
        ret[con.id] = [];
      }
      ret[con.id].push({
        id: Buffer.from(ccpag.id, 'base64').toString('binary') as string,
        status: con.CONNSTATUS
      });
    })
  });
  return ret;
}

export const buildNetworkTopologyData = (
  input: { element: NetworkTopologyElementsEnum, data: unknown[] }[],
  mapConfig?: NetworkTopologyMapConfig,
): NetworkTopologyMapData => {

  const dataMap = buildElementsMap(cloneDeep(input));

  const [defaultLatitude, defaultLongitude] = mapConfig?.mapPosition.center || [DEFAULT_US_CENTER_LATITUDE, DEFAULT_US_CENTER_LONGITUDE];
  const defaultLocation = { latitude: defaultLatitude, longitude: defaultLongitude };

  const locationsMapBuilder = new LocationsMapBuilder(dataMap.location as NetworkTopologyLocation[]);
  const supportedFeatureConfig = getSupportedFeatureConfig(dataMap);
  // const mockCcpagMdf3Map = setccpagMdf3Map(dataMap.ccpagMdf3Map);
  let upfToCcpagConnectionMap: { [key: string]: { id: string, status: string }[] } = {};
  let ccpagToMdf3ConnectionMap: { [key: string]: { id: string, status: string }[] } = {};

  if (dataMap.ccpagConnectionDetails) {
    const { ccpagMdf3Map, upfToCcpagMap } = buildCcpagDetailMap(dataMap.ccpagConnectionDetails as CcpagDetail[]);
    upfToCcpagConnectionMap = upfToCcpagMap;
    ccpagToMdf3ConnectionMap = ccpagMdf3Map;
  }

  setAdmfLeader(dataMap);

  const collectionFunctions = buildNetworkTopologyElements(
    dataMap.cf as any[],
    NetworkTopologyElementsEnum.CollectionFunction,
    locationsMapBuilder,
    supportedFeatureConfig.collectionFunctions
    );

  const afs = buildNetworkTopologyElements(
    getAfsBasedOnNetworkGeneration(dataMap.af as AccessFunction[], supportedFeatureConfig.accessFunctions, supportedFeatureConfig.features, dataMap.configuredAfs) as any[],
    NetworkTopologyElementsEnum.AccessFunction,
    locationsMapBuilder,
    supportedFeatureConfig.accessFunctions
  );

  const xcipios = buildNetworkTopologyElements(
    dataMap.xcipio as any[],
    NetworkTopologyElementsEnum.Xcipio,
    locationsMapBuilder,
  );

  const {
 xcipioNodes, ccpags, mdf2s, mdf3s, admfs
} = mapXcipioNodes(xcipios, ccpagToMdf3ConnectionMap, afs, collectionFunctions, locationsMapBuilder, defaultLocation);
  const accessFunctions = mapAfs(afs, [...xcipioNodes, ...ccpags, ...mdf2s, ...mdf3s, ...admfs], dataMap.poiXcipioNodeMap, locationsMapBuilder, supportedFeatureConfig.accessFunctions, upfToCcpagConnectionMap);

  return {
    accessFunctions,
    collectionFunctions,
    xcipioNodes,
    ccpags,
    mdf2s,
    mdf3s,
    admfs
  };
};

const setAdmfLeader = (dataMap: {[key in string]: unknown[]}) => {
  const admfStatuses = (dataMap?.AdmfLeader?.[0] as AdmfLeader)?.admfstatuslist ?? [];
  const ipToStatusMap = admfStatuses.reduce((acc, curr: AdmfStatus) => {
    if (!acc[curr.vip]) {
      acc[curr.vip] = curr.status;
    }
    return acc;
  }, {} as {[key: string]: string})

  dataMap.xcipio
    .filter((x: unknown) => (x as RegisteredNode).nodeType.toLocaleUpperCase() === 'ADMF')
    .forEach((x: unknown) => {
      if (_.has(ipToStatusMap, (x as NodeData).ipAddress)) {
        (x as NodeData).status = ipToStatusMap[(x as NodeData).ipAddress];
      }
    });
}

const setPoiDataConnections = (
  poiMap: { [key: string]: string[] },
  poiItem: PoiXcipioNodeMap,
): string[] => {
  const poiElement = poiMap[poiItem.poiId];

  if (poiElement?.length) {
    return poiElement;
  }

  return [];
}

const setPoiXcipioNodeRelationMap = (
  poiXcipioNodeMap: PoiXcipioNodeMap[],
  xcipioNodes: NetworkTopologyElement[],
  supportedAccessFunctions: any
): {[key: string]: string[]} => {
  const mapData = poiXcipioNodeMap
    .reduce((poiMap, poiItem: PoiXcipioNodeMap) => {
      const currrentItem = setPoiDataConnections(poiMap, poiItem);
      currrentItem.push(poiItem.destNodeName);
      return ({
        ...poiMap,
        [poiItem.poiId]: currrentItem
      });
  }, {} as {[key: string]: string[]});

  return mapData;
}

const mapXcipioNodes = (
    xcipioNodes: NetworkTopologyElement[],
    ccpagMdf3Map: { [key: string]: { id: string, status: string }[] },
    afs: NetworkTopologyElement[],
    cfs: NetworkTopologyElement[],
    locationsMapBuilder: LocationsMapBuilder,
    defaultLocation: { latitude: number, longitude: number }): { xcipioNodes:NetworkTopologyElement[], ccpags: NetworkTopologyElement[], mdf2s: NetworkTopologyElement[], mdf3s: NetworkTopologyElement[], admfs: NetworkTopologyElement[] } => {
  const ccpags = xcipioNodes.filter((x) => x.data.nodeType === 'CCPAG');
  const xcipios = xcipioNodes.filter((x) => x.data.nodeType !== 'CCPAG');
  let mappedXcipios = mapXcipios(xcipios, afs, cfs, locationsMapBuilder, defaultLocation);
  const mdf3s = mappedXcipios.filter((x) => x.data.nodeType === 'MDF3');
  const mappedCcpags = mapCcpags(ccpags, mdf3s, ccpagMdf3Map, locationsMapBuilder, defaultLocation);
  mappedXcipios.filter((mappedXcipio) => mappedXcipio.data.nodeType === 'ADMF').map((admf) => {
    [...mappedXcipios, ...mappedCcpags].filter((xcipio) => xcipio.data.nodeType !== 'ADMF').forEach((xcipio) => {
        if (admf.connections) {
          if (xcipio.location && xcipio.location.latitude && xcipio.location.longitude) {
            admf.connections[NetworkTopologyElementsEnum.Xcipio].push(buildConnection(admf, xcipio.status, xcipio));
          }
        }
      })
  })
  const mdf2s = mappedXcipios.filter((x) => x.data.nodeType === 'MDF2');
  const admfs = mappedXcipios.filter((x) => x.data.nodeType === 'ADMF');
  mappedXcipios = mappedXcipios.filter((x) => x.data.nodeType === 'XCP');
  return {
    xcipioNodes: mappedXcipios,
    ccpags: mappedCcpags,
    mdf2s,
    mdf3s,
    admfs
  };
}

const mapXcipios = (xcipioNodes: NetworkTopologyElement[], afs: NetworkTopologyElement[], cfs: NetworkTopologyElement[], locationsMapBuilder: LocationsMapBuilder, defaultLocation: { latitude: number, longitude: number }) => {
  return (xcipioNodes)
  .map((x) => {
    const ret = { ...x };
    ret.location = getXcipioLocation(x, locationsMapBuilder, defaultLocation);// TODO: location needs to come from timezone, statename, city
    ret.connections = {
      [NetworkTopologyElementsEnum.CollectionFunction]: [],
      [NetworkTopologyElementsEnum.Xcipio]: [],
      [NetworkTopologyElementsEnum.AccessFunction]: []
    }

    if (x.data.nodeType === 'ADMF' || x.data.nodeType === 'XCP') {
      afs
      .filter((af) => af.poiType !== 'UPF')
      .forEach((af) => {
        if (ret.connections) {
          if (af.data.provisioningInterfaces && af.data.provisioningInterfaces.length > 0) {
            if (af.location && af.location.latitude && af.location.longitude) {
              ret.connections[NetworkTopologyElementsEnum.AccessFunction].push(buildConnection(x, af.status, af));
            }
          }
        }
      });
    }
    cfs.forEach((cf) => {
      if (cf.data.hi2Interfaces && cf.data.hi2Interfaces.length > 0) {
        addCfConnection(ret, ['MDF2', 'XCP'], cf);
      }
      if (cf.data.hi3Interfaces && cf.data.hi3Interfaces.length > 0) {
        addCfConnection(ret, ['MDF3'], cf);
      }
    });
    return ret;
  });
}

const mapCcpags = (ccpags: NetworkTopologyElement[], mdf3s: NetworkTopologyElement[], ccpagMdf3Map: { [key: string]: { id: string, status: string }[] }, locationsMapBuilder: LocationsMapBuilder, defaultLocation: { latitude: number, longitude: number }) => {
  return (ccpags)
  .map((x) => {
    const ret = { ...x };
    ret.location = getXcipioLocation(x, locationsMapBuilder, defaultLocation);// TODO: location needs to come from timezone, statename, city
    ret.connections = {
      [NetworkTopologyElementsEnum.CollectionFunction]: [],
      [NetworkTopologyElementsEnum.Xcipio]: [],
      [NetworkTopologyElementsEnum.AccessFunction]: []
    }

    if (ccpagMdf3Map[x.data.id]) {
      ccpagMdf3Map[x.data.id].forEach(({ id, status }) => {
        const mdf3 = mdf3s.find((n) => n.data?.id === id) as any;
        if (mdf3) {
          ret.connections?.[NetworkTopologyElementsEnum.Xcipio].push(buildConnection(ret, status, mdf3));
        }
      });
    }
    return ret;
  });

}

const addCfConnection = (xcipio: any, xcipioNodeTypes: string[], cf: any): void => {
  if (!xcipioNodeTypes.includes(xcipio.data.nodeType)) {
    return;
  }
  if (xcipio.connections && xcipio.connections[NetworkTopologyElementsEnum.CollectionFunction]) {
    if (cf.location && cf.location.latitude && cf.location.longitude) {
      const con = {
        status: getCfStatus(xcipio.data.nodeType, cf.data),
        destination: { latitude: cf.location.latitude, longitude: cf.location.longitude },
        destinationId: cf.data.id,
        sourceType: NetworkTopologyElementsEnum.Xcipio,
        destType: NetworkTopologyElementsEnum.CollectionFunction,
        tooltipData: []
      } as Connection;
      xcipio.connections[NetworkTopologyElementsEnum.CollectionFunction].push(con);
    }
  }
};

const getCfStatus = (xcipioNodeType: 'MDF2' | 'MDF3' | 'XCP', cfData: CollectionFunction): string => {
  let ret = 'UNKNOWN';
  if (xcipioNodeType === 'MDF2' || xcipioNodeType === 'XCP') {
    if (cfData.hi2Interfaces && cfData.hi2Interfaces.length > 0) {
      ret = getInterfaceState(cfData.hi2Interfaces);
    }
  } else if (xcipioNodeType === 'MDF3') {
    if (cfData.hi3Interfaces && cfData.hi3Interfaces.length > 0) {
      ret = getInterfaceState(cfData.hi3Interfaces);
    }
  }
  return ret;
};

const mapAfs = (accessFunctions: NetworkTopologyElement[], xcipioNodes: NetworkTopologyElement[], poiXcipioNodeMap: unknown[], locationsMapBuilder: LocationsMapBuilder, supportedAccessFunctions: any, upfToCcpagMap: { [key: string]: { id: string, status: string }[] }) => {
  const afPoiXcipioNodeMap = setPoiXcipioNodeRelationMap(poiXcipioNodeMap as any[], xcipioNodes, supportedAccessFunctions);
  const xcp = xcipioNodes.find((x) => (x as any).data.nodeType === 'XCP') as any;

  return (accessFunctions).map((af) => {
    const ret = { ...af };
    const afLocation = locationsMapBuilder.getLocation(af.data.timeZone, af.data.stateName, af.data.city, af.data.latitude, af.data.longitude);
    ret.location = afLocation
    ret.connections = {
      [NetworkTopologyElementsEnum.Xcipio]: []
    };
    if (afLocation) {
      if (xcp) {
        ret.connections[NetworkTopologyElementsEnum.Xcipio].push(buildConnection(af, af.status, xcp));
      }
      // ADMF, CCPAG, MDF2 or MDF3 connections
      if (afPoiXcipioNodeMap[af.data.id]) {
        const nodeNames = afPoiXcipioNodeMap[af.data.id];
        const node = xcipioNodes.find((xcipioNode) => nodeNames.includes((xcipioNode as any)?.data?.nodeName)) as any;
        const xcipioNodesToConnect = xcipioNodes.filter((xcipioNode) => nodeNames.includes((xcipioNode as any).data?.nodeName)) as any;

        if (!node || (node?.data?.nodeType === 'ADMF' && af?.poiType === 'UPF')) {
          return ret;
        }

        if (node) {
          xcipioNodesToConnect.forEach((nodeItem: any) => {
            ret.connections?.[NetworkTopologyElementsEnum.Xcipio].push(buildConnection(af, af.status, nodeItem));
          });
        }
      }
      // connections to ccpag
      if (upfToCcpagMap[af.data.id]) {
        if (upfToCcpagMap[af.data.id].length > 0) {
          ret.status = NetworkTopologyStateEnum[upfToCcpagMap[af.data.id][0].status as keyof typeof NetworkTopologyStateEnum];
        }
        upfToCcpagMap[af.data.id].forEach(({ id, status }) => {
          const node = xcipioNodes.find((xcipioNode) => xcipioNode.data?.id === id) as any;
          if (node) {
            ret.connections?.[NetworkTopologyElementsEnum.Xcipio].push(buildConnection(af, status, node));
          }
        });
      }
    }
    return ret;
  });
}

const buildConnection = (srcElement: NetworkTopologyElement, status: any, destElement: any): Connection => {
  return {
    destination: { latitude: destElement.location.latitude, longitude: destElement.location.longitude },
    status,
    destinationId: destElement.data.id,
    sourceType: srcElement.type,
    destType: destElement.type
  };
};

const buildNetworkTopologyElements = (
  data: { timeZone: string, city: string, stateName: string, latitude:string, longitude: string, id: string}[],
  type: NetworkTopologyElementsEnum,
  mapBuilder: LocationsMapBuilder,
  supportedFeatureConfig?: { accessFunctions: any, collectionFunctions: any }
):NetworkTopologyElement[] => {

  return data.map((item) => {
    const location = mapBuilder.getLocation(item.timeZone, item.stateName, item.city, item.latitude, item.longitude);
    const data = { ...item, id: Buffer.from(item.id, 'base64').toString('binary') };
    const status = setNetworkTopologyStatus(type, item as AccessFunction | CollectionFunction | NodeData);
    const poiType = getPoiType(type, item as AccessFunction | CollectionFunction, supportedFeatureConfig);
    const poiVendor = getPoiVendor(type, item as AccessFunction | CollectionFunction, supportedFeatureConfig);

    return ({
      type,
      location,
      data,
      status,
      poiType,
      poiVendor,
    })
  });
}

const buildElementsMap = (
  data: { element: NetworkTopologyElementsEnum, data: unknown[] }[]
): {[key in NetworkTopologyElementsEnum]: unknown[]} => {
  return data.reduce((total, actual) => ({
    ...total,
    [actual.element]: actual.data
  }), {}) as {[key in NetworkTopologyElementsEnum]: unknown[]};
}

const getConnectionState = (states: string[]) => {
  if (states.find((state) => state === NetworkTopologyStateEnum.FAILED)){
    return NetworkTopologyStateEnum.FAILED
  }

  if (states.find((state) =>
    state === NetworkTopologyStateEnum.INACTIVE ||
    state === NetworkTopologyStateEnum.BLOCKED)
  ){
    return NetworkTopologyStateEnum.INACTIVE
  }

  if (
    states.find((state) =>
        state === NetworkTopologyStateEnum.IDLE ||
        state === NetworkTopologyStateEnum.ACTIVE)
  ) {
    return NetworkTopologyStateEnum.ACTIVE;
  }

  return NetworkTopologyStateEnum.UNKNOWN;
}

export const getInterfaceState = (accessFunctionInterface: (AccessFunctionInterface | CollectionFunctionInterface)[]) => {
  const connections = accessFunctionInterface.map((interfaceItem) => (interfaceItem.options || []).find((option) => option.key === 'state')?.value);
  if (connections.length === 0) {
    return NetworkTopologyStateEnum.UNKNOWN;
  }
  return getConnectionState(connections as string[]);
}

const setNetworkTopologyStatus = (
  type: NetworkTopologyElementsEnum,
  data: AccessFunction | CollectionFunction | NodeData,
) => {
  if (type === NetworkTopologyElementsEnum.Xcipio) {
    return getXcipioNodeStatusEnum((data as NodeData)?.status || '');
  }

  if (type !== NetworkTopologyElementsEnum.AccessFunction && type !== NetworkTopologyElementsEnum.CollectionFunction) {
    return NetworkTopologyStateEnum.ACTIVE;
  }

  if (type === NetworkTopologyElementsEnum.AccessFunction) {
    return getConnectionState([
      getInterfaceState((data as AccessFunction).provisioningInterfaces || []),
      getInterfaceState((data as AccessFunction).dataInterfaces || []),
      getInterfaceState((data as AccessFunction).contentInterfaces || [])
    ]);
  }

  return getConnectionState([getInterfaceState((data as CollectionFunction).hi2Interfaces || [])]);
}

const getXcipioNodeStatusEnum = (status: string) => {
  if (!status) {
    return NetworkTopologyStateEnum.UNKNOWN;
  }
  if (status.toLocaleUpperCase() === 'ACTIVE' || status.toLocaleUpperCase() === 'PARTIALLY_ACTIVE') {
    return NetworkTopologyStateEnum.ACTIVE;
  }
  if (status.toLocaleUpperCase() === 'FAILED') {
    return NetworkTopologyStateEnum.FAILED;
  }
  if (status.toLocaleUpperCase() === 'INACTIVE') {
    return NetworkTopologyStateEnum.INACTIVE;
  }
  if (status.toLocaleUpperCase() === 'LEADER') {
    return NetworkTopologyStateEnum.LEADER;
  }
  return NetworkTopologyStateEnum.UNKNOWN;
}

const getPoiType = (
  type: NetworkTopologyElementsEnum,
  data: AccessFunction | CollectionFunction,
  supportedFeatureConfig?: { [key: string]: { type: string} }
) => {
  if (
    supportedFeatureConfig == null || (
      type !== NetworkTopologyElementsEnum.AccessFunction &&
      type !== NetworkTopologyElementsEnum.CollectionFunction
    )
  ) {
    return '';
  }
  return supportedFeatureConfig?.[data.tag]?.type || '';
}

const getPoiVendor = (
  type: NetworkTopologyElementsEnum,
  data: AccessFunction | CollectionFunction,
  supportedFeatureConfig?: { [key: string]: { vendor: string} }
) => {
  if (
    supportedFeatureConfig == null || (
      type !== NetworkTopologyElementsEnum.AccessFunction &&
      type !== NetworkTopologyElementsEnum.CollectionFunction
    )
  ) {
    return '';
  }

  if (type === NetworkTopologyElementsEnum.AccessFunction) {
    return supportedFeatureConfig?.[data.tag]?.vendor || '';
  }

  return supportedFeatureConfig?.[data.type]?.vendor || '';
}

const getSupportedFeatureConfig = (dataMap: {[key in NetworkTopologyElementsEnum]: any[]}) => {
  return {
    accessFunctions: dataMap.supportedFeature[0].config.accessFunctions,
    collectionFunctions: dataMap.supportedFeature[0].config.collectionFunctions,
    features: dataMap.supportedFeature[0].features.nodes[0].features
  }
}

const getXcipioLocation = (
  networkTopologyElement: NetworkTopologyElement,
  locationsMapBuilder: LocationsMapBuilder,
  defaultLocation: { latitude: number, longitude: number },
) => {
  const { country, stateCode, city } = networkTopologyElement.data;

  if (country && stateCode && city) {
    return locationsMapBuilder.getLocation('America/New_York', stateCode, city);
  }

  if (
    networkTopologyElement?.data?.latitude != null &&
    networkTopologyElement?.data?.longitude != null &&
    networkTopologyElement?.data?.latitude !== '' &&
    networkTopologyElement?.data?.longitude !== ''
  ) {
    return {
      latitude: networkTopologyElement.data?.latitude,
      longitude: networkTopologyElement.data?.longitude,
    }
  }

  return {
    latitude: defaultLocation.latitude,
    longitude: defaultLocation.longitude,
    city: undefined,
    stateCode: undefined
  }
}

export function adaptCustomFilterServerResponse(payload: NetworkTopologyFilterServerPayload[]): NetworkTopologyFilter[] {
  return payload.map((p) => {
    const obj = JSON.parse(p.filterJson) as NetworkTopologyFilter;
    obj.id = p.id;
    return obj;
  });
}

export class LocationsMapBuilder {
  stateCodeMap: Map<string, {
    id: string | number,
    country: string,
    stateCode: string,
    stateName: string,
    citiesMap: Map<string, NetworkTopologyLocationCity>
  }>;

  timezoneClusterMap: Map<string, {
    latitude: number,
    longitude: number,
    timezone: string,
  }>;

  constructor(readonly locations: NetworkTopologyLocation[]) {
    this.stateCodeMap = this.buildDataLocationMap(locations);
    this.timezoneClusterMap = this.buildTimezoneLocationMap(locations);
  }

  private buildTimezoneLocationMap(locations: NetworkTopologyLocation[]): Map<string, {
    latitude: number,
    longitude: number,
    timezone: string,
  }>{

    const clusterMap = locations
      .map((loc) => loc.cities)
      .reduce((total, actual) => [...total, ...actual], [])
      .reduce((total, city) => {
        const timezoneGroup = total[city.timezone] || { latitude: 0, longitude: 0, count: 0 };

        return {
          ...total,
          [city.timezone]: {
            latitude: timezoneGroup.latitude + city.latitude,
            longitude: timezoneGroup.longitude + city.longitude,
            count: timezoneGroup.count + 1
          },
        }
      }, {} as {[key: string]: {latitude: number, longitude: number, count: number} });

    return new Map(Object.keys(clusterMap).map((timezone) => ([timezone, {
      timezone,
      latitude: clusterMap[timezone].latitude / clusterMap[timezone].count,
      longitude: clusterMap[timezone].longitude / clusterMap[timezone].count,
    }])));
  }

  private buildDataLocationMap(locations: NetworkTopologyLocation[]){
    return new Map(locations.map((location) => [location.stateName, {
      id: location.id,
      country: location.country,
      stateCode: location.stateCode,
      stateName: location.stateName,
      citiesMap: new Map(location.cities.map((city) => [city.city, {
        ...city,
        stateCode: location.stateCode
      }]))
    }]));
  }

  getState(stateCode:string) {
    return this.stateCodeMap.get(stateCode);
  }

  getTimezoneCenter(timeZone:string) {
    const location = this.timezoneClusterMap.get(timeZone);
    if (!location) {
      return undefined;
    }
    return {
      latitude: location.latitude,
      longitude: location.longitude,
    };
  }

  getLocation(timeZone: string, stateCode: string, city:string, latitude?:string, longitude?:string) {

    if (latitude != null && longitude != null && latitude !== '' && longitude !== '') {
      return {
        latitude,
        longitude,
      };
    }

    const stateData = this.getState(stateCode);

    if (!stateData) {
      return this.getTimezoneCenter(timeZone);
    }

    return stateData?.citiesMap.get(city);
  }
}

export function filterNetworkTopologyData(
  payload: {
    filter: NetworkTopologyFilter,
    availableAccessFunction: SupportedFeaturesAccessFunctions,
    availableCollectionFunction: SupportedFeaturesCollectionFunctions,
  },
  originalMapData: NetworkTopologyMapData,
): { filteredMapData: NetworkTopologyMapData, filterLocation?: { latitude: number, longitude: number, zoom: number } } {
  const { filter, availableAccessFunction, availableCollectionFunction } = payload;

  let filteredMapData = cloneDeep(originalMapData);
  let filterLocation;

  if (Object.values(filter).every((filter) => filter === '' || filter.length === 0)) {
    return {
      filteredMapData,
    };
  }

  if (filter.location.length) {
    filteredMapData = filterByLocation(cloneDeep(filteredMapData), filter);
    filterLocation = getFilterLocationCenter(
      originalMapData.accessFunctions,
      originalMapData.collectionFunctions,
      filter
    );
  }

  if (filter.poiType.length) {
    filteredMapData = filterByPoiType(cloneDeep(filteredMapData), filter, availableAccessFunction, availableCollectionFunction);
  }

  if (filter.poiVendor.length) {
    filteredMapData = filterByVendors(cloneDeep(filteredMapData), filter, availableAccessFunction);
  }

  if (filter.cfType.length) {
    filteredMapData = filterByCfType(cloneDeep(filteredMapData), filter);
  }

  if (filter.search.length) {
    filteredMapData = filterBySearch(cloneDeep(filteredMapData), filter, availableAccessFunction);
  }

  if (filter.xcipioService.length) {
    filteredMapData = filterByXcipioService(cloneDeep(filteredMapData), filter);
  }

  if (filter.id?.length && filter.poiDataType?.length) {
    const graph = new NeGraph(filteredMapData);
    filteredMapData = graph.getAllNodesInPathFromNodeToLeaf(filter.id);
    // filteredMapData = filterByPoiId(cloneDeep(filteredMapData), filter);
  }

  return {
    filteredMapData,
    filterLocation
  };
}

const getFilterLocationCenter = (accessFunctions: any[], collectionFunctions: any[], filter: NetworkTopologyFilter) => {
  const loncation = [
    ...accessFunctions,
    ...collectionFunctions
  ]
  .find((d) => isValueIncluded(d.location?.city, filter.location))?.location;

  if (!loncation) {
    return undefined
  }

  return {
    latitude: loncation.latitude,
    longitude: loncation.longitude,
    zoom: 6
  }
}

const getNonRepeatedPoiList = (data: any[]) => {
  const poiMap = new Map(data.map((item) => [item.data.id, item]));
  return Array.from(poiMap.values());
}

const filterByXcipioService = (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter,
):NetworkTopologyMapData => {
  const filteredXcipioNodes = filterXcipioNodesByType(mapData.xcipioNodes, filter.xcipioService);
  let filteredCcpags = filterXcipioNodesByType(mapData.ccpags, filter.xcipioService);
  let filteredMDF2s = filterXcipioNodesByType(mapData.mdf2s, filter.xcipioService);
  let filteredMDF3s = filterXcipioNodesByType(mapData.mdf3s, filter.xcipioService);
  const filteredADMFs = filterXcipioNodesByType(mapData.admfs, filter.xcipioService);
  const filteredCcpagNodeConnections = getXcipioNodesByAfCf(mapData.xcipioNodes, filteredCcpags);
  const nonCcpagsFiltered = [...filteredXcipioNodes, ...filteredMDF2s, ...filteredMDF3s, ...filteredADMFs];
  let accessFunctions = filterAfCfByXcipioNodes('af', nonCcpagsFiltered, mapData.accessFunctions);
  const ccpagAccessFunctions = filterAfCfByXcipioNodes('af', filteredCcpags, mapData.accessFunctions);
  let collectionFunctions = filterAfCfByXcipioNodes('cf', nonCcpagsFiltered, mapData.collectionFunctions);
  const ingressAccessFunctions = filterIncomingAfs(nonCcpagsFiltered, mapData.accessFunctions);
  const ingressCCpags = filterIncomingCcpags(mapData.ccpags, nonCcpagsFiltered);
  const xcipiosLabels = filter.xcipioService.map((x) => x.label);
  let outgoingMdf3s = [];

  if (xcipiosLabels.includes('MDF3')) {
    // get ingress to ccpags
    const afs = filterIncomingAfs(ingressCCpags, mapData.accessFunctions);
    accessFunctions = [...accessFunctions, ...afs]
  }
  if (xcipiosLabels.includes('CCPAG')) {
    const inAfs = filterIncomingAfs(filteredCcpags, mapData.accessFunctions);
    // outgoing MDF3s
    outgoingMdf3s = getXcipioNodesByAfCf(mapData.mdf3s, filteredCcpags);
    const outgoingCfs = filterAfCfByXcipioNodes('cf', outgoingMdf3s, mapData.collectionFunctions);
    accessFunctions = [...accessFunctions, ...inAfs];
    collectionFunctions = [...collectionFunctions, ...outgoingCfs]
  }

  if (xcipiosLabels.includes('ADMF')) {
    const filteredXcipioNodes = getXcipioNodesByAfCf(mapData.xcipioNodes, accessFunctions);
    const outcfs = filterAfCfByXcipioNodes('cf', filteredXcipioNodes, mapData.collectionFunctions);
    // filteredXcipioNodes = [...nonCcpagsFiltered, ...outgoingXcipios];
    collectionFunctions = [...collectionFunctions, ...outcfs];
    filteredCcpags = [...filteredCcpags, ...mapData.ccpags];
    filteredMDF2s = [...filteredMDF2s, ...mapData.mdf2s];
    filteredMDF3s = [...filteredMDF3s, ...mapData.mdf3s];
  }
  return {
    ...mapData,
    xcipioNodes: getNonRepeatedPoiList([...filteredXcipioNodes, ...filteredCcpagNodeConnections]),
    accessFunctions: getNonRepeatedPoiList([...accessFunctions, ...ccpagAccessFunctions, ...ingressAccessFunctions]),
    collectionFunctions: getNonRepeatedPoiList([...collectionFunctions]),
    ccpags: getNonRepeatedPoiList([...filteredCcpags, ...ingressCCpags]),
    mdf2s: getNonRepeatedPoiList([...filteredMDF2s]),
    mdf3s: getNonRepeatedPoiList([...filteredMDF3s, ...outgoingMdf3s]),
    admfs: getNonRepeatedPoiList([...filteredADMFs])
  }
}

const filterIncomingCcpags = (
  ccpags: any[],
  xcipioNodes: any[],
) => {
  const nodeAfsConnectionsMap = ccpags
    .reduce((total, node) => ({
      ...total,
      [node.data.id]: node.connections.xcipio.reduce((afMap: any, af: any) => ({ ...afMap, [af.destinationId]: true }), {})
  }), {});

  return ccpags.filter((item) => {
    return xcipioNodes.some((node) => nodeAfsConnectionsMap[item.data.id][node.data.id]);
  });
}

const filterIncomingAfs = (
  filteredXcipioNodes: any[],
  accessFunctions: any[],
) => {
  const nodeAfsConnectionsMap = accessFunctions
    .reduce((total, node) => ({
      ...total,
      [node.data.id]: node.connections.xcipio.reduce((afMap: any, af: any) => ({ ...afMap, [af.destinationId]: true }), {})
  }), {});

  return accessFunctions.filter((item) => {
    return filteredXcipioNodes.some((node) => nodeAfsConnectionsMap[item.data.id][node.data.id]);
  });
}

const filterByPoiId = (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter,
):NetworkTopologyMapData => {

  if (filter.poiDataType === 'xcipio') {
    const filteredXcipioNodes = mapData.xcipioNodes
      .filter((xcipioNode) => xcipioNode.data.id.toLocaleLowerCase() === filter.id?.toLocaleLowerCase());

    const filteredCcpags = mapData.ccpags
      .filter((ccpagNode) => ccpagNode.data.id.toLocaleLowerCase() === filter.id?.toLocaleLowerCase());

    const filteredCcpagNodeConnections = getXcipioNodesByAfCf(mapData.xcipioNodes, filteredCcpags);
    const accessFunctions = filterAfCfByXcipioNodes('af', filteredXcipioNodes, mapData.accessFunctions);
    const ccpagAccessFunctions = filterAfCfByXcipioNodes('af', filteredCcpags, mapData.accessFunctions);
    const collectionFunctions = filterAfCfByXcipioNodes('cf', filteredXcipioNodes, mapData.collectionFunctions);

    return {
      ...mapData,
      xcipioNodes: getNonRepeatedPoiList([...filteredXcipioNodes, ...filteredCcpagNodeConnections]),
      accessFunctions: getNonRepeatedPoiList([...accessFunctions, ...ccpagAccessFunctions]),
      collectionFunctions,
      ccpags: filteredCcpags
    }
  }

  if (filter.poiDataType === 'af') {
    const accessFunctions = cloneDeep(mapData).accessFunctions.filter((af) => af.data.id.toLocaleLowerCase() === filter.id?.toLocaleLowerCase());
    const ccpags = getXcipioNodesFromConnections(accessFunctions, mapData.ccpags);
    const xcipioNodes = getXcipioNodesFromConnections([...accessFunctions, ...ccpags], mapData.xcipioNodes);
    const collectionFunctions = getCollectionFunctionsFromConnections(xcipioNodes, mapData.collectionFunctions)

    return {
      ...mapData,
      accessFunctions,
      collectionFunctions,
      xcipioNodes,
      ccpags,
    }
  }

  if (filter.poiDataType === 'cf') {
    const filteredCFs = mapData.collectionFunctions.filter((cf) => cf.data.id.toLocaleLowerCase() === filter.id?.toLocaleLowerCase());
    const filteredNodesByCf = getXcipioNodesByCf(mapData.xcipioNodes, filteredCFs);
    const filteredccpagsNodesByCf = getXcipioNodesByCf(mapData.ccpags, filteredCFs);

    return {
      ...mapData,
      accessFunctions: [],
      collectionFunctions: filteredCFs,
      xcipioNodes: filteredNodesByCf,
      ccpags: filteredccpagsNodesByCf,
    }
  }

  return {
    ...mapData,
  }
}

const filterXcipioNodesByType = (
  xcipioNodes: any[],
  xcipioService: { value: string, label: string }[]
): any[] => {
  return xcipioNodes.filter((xcipioNode) => isValueIncluded(xcipioNode.data.nodeType, xcipioService));
}

const filterAfCfByXcipioNodes = (
  poi: 'af' | 'cf' | 'xcipio',
  filteredXcipioNodes: any[],
  accessFunctions: any[],
): any[] => {
  const nodeAfsConnectionsMap = filteredXcipioNodes
    .reduce((total, node) => ({
      ...total,
      [node.data.id]: node.connections[poi].reduce((afMap: any, af: any) => ({ ...afMap, [af.destinationId]: true }), {})
  }), {});

  return accessFunctions.filter((item) => {
    return filteredXcipioNodes.some((node) => nodeAfsConnectionsMap[node.data.id][item.data.id]);
  });
}

const filterBySearch= (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter,
  availableAccessFunction?: SupportedFeaturesAccessFunctions,
  ): NetworkTopologyMapData => {
  const searchTxt = filter.search.toLocaleLowerCase().trim();

  const accessFunctionsFilter = mapData.accessFunctions
    .filter(filterPoiBySearch(searchTxt, availableAccessFunction));
  const collectionFunctionsFilter = mapData.collectionFunctions
    .filter(filterPoiBySearch(searchTxt));
  const xcipioNodesFilter = mapData.xcipioNodes
    .filter(filterPoiBySearch(searchTxt, availableAccessFunction));
  const ccpagsFilter = mapData.ccpags
    .filter(filterPoiBySearch(searchTxt, availableAccessFunction));

  const filteredCcpagsByAfs = getXcipioNodesFromConnections(accessFunctionsFilter, mapData.ccpags);
  const filteredXcipionodesByAfs = getXcipioNodesFromConnections([...accessFunctionsFilter, ...filteredCcpagsByAfs, ...ccpagsFilter], mapData.xcipioNodes);

  const filteredXcipioNodesByCf = getXcipioNodesByCf(mapData.xcipioNodes, collectionFunctionsFilter);
  const filteredCcpagsNodesByCf = getXcipioNodesByCf(mapData.ccpags, collectionFunctionsFilter);

  const collectionFunctionsFromXcipio = getCollectionFunctionsFromConnections(cloneDeep(filteredXcipionodesByAfs), mapData.collectionFunctions)
  const accessFunctionsFromXcipio = getCollectionFunctionsFromConnections(cloneDeep(filteredXcipionodesByAfs), mapData.accessFunctions)

  return {
    ...mapData,
    accessFunctions: flatPoiList([...accessFunctionsFilter, ...accessFunctionsFromXcipio]),
    collectionFunctions: flatPoiList([...collectionFunctionsFilter, ...collectionFunctionsFromXcipio]),
    xcipioNodes: flatPoiList([...xcipioNodesFilter, ...filteredXcipionodesByAfs, ...filteredXcipioNodesByCf]),
    ccpags: flatPoiList([...ccpagsFilter, ...filteredCcpagsByAfs, ...filteredCcpagsNodesByCf]),
  }
}

const filterByCfType = (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter,
):NetworkTopologyMapData => {

  const cfsFilteredByType = mapData.collectionFunctions.filter((cf) => isValueIncluded(cf.data.type, filter.cfType));
  const xcipioWithConnectionsToCfs = getXcipioNodesByCf(mapData.xcipioNodes, cfsFilteredByType);
  const ccpagsWithConnectionsToCfs = getXcipioNodesByCf(mapData.ccpags, cfsFilteredByType);
  const ingressAccessFunctions = filterIncomingAfs([...xcipioWithConnectionsToCfs, ...ccpagsWithConnectionsToCfs], mapData.accessFunctions);

  return {
    ...mapData,
    accessFunctions: ingressAccessFunctions,
    collectionFunctions: cfsFilteredByType,
    xcipioNodes: xcipioWithConnectionsToCfs,
    ccpags: ccpagsWithConnectionsToCfs,
  }
}

const isValueIncluded = (value:string, filters: { value: string, label: string }[]) => {
  return filters.some((filter) => filter.value?.toLocaleLowerCase() === value?.toLocaleLowerCase());
}

const filterByLocation = (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter
):NetworkTopologyMapData => {

  const afsFilteredByLocation = mapData.accessFunctions.filter((af) => isValueIncluded(af.data.city, filter.location));
  const cfsFilteredByLocation = mapData.collectionFunctions.filter((cf) => isValueIncluded(cf.data.city, filter.location));
  const ccpagsFilteredByLocation = mapData.ccpags.filter((ccpag) => isValueIncluded(ccpag.data.city, filter.location));
  const xcipioNodesFromLocationConnections = getXcipioNodesByAfCf(
    mapData.xcipioNodes,
    [...afsFilteredByLocation, ...cfsFilteredByLocation, ...ccpagsFilteredByLocation]
  );

  const poisFromXcipioNode = getAfsCfsCcpagsFromXcipioNodes(
    xcipioNodesFromLocationConnections,
    mapData.accessFunctions,
    mapData.collectionFunctions,
    mapData.ccpags
  );

  const nodesConnectedToCfs = getXcipioNodesByCf(
    mapData.xcipioNodes,
    flatPoiList([...cfsFilteredByLocation, ...poisFromXcipioNode.cfs])
  );

  const filteredAFs = flatPoiList([...afsFilteredByLocation, ...poisFromXcipioNode.afs]);
  const filteredCFs = flatPoiList([...cfsFilteredByLocation, ...poisFromXcipioNode.cfs]);
  const filteredCcpags = flatPoiList([...ccpagsFilteredByLocation, ...poisFromXcipioNode.ccpags]);

  const xcipioNodesWithAfsConnected = getXcipioNodesWhereAfsAreConnected(mapData.xcipioNodes, filteredAFs)

  const filteredNodes = setXcipioNodeConnections(
    flatPoiList(
      [
        ...xcipioNodesFromLocationConnections,
        ...nodesConnectedToCfs,
        ...xcipioNodesWithAfsConnected
      ]
    ),
    filteredAFs,
    filteredCFs,
  );

  // Access Functions connected to the xcipio nodes conncted to the original location POI
  // const ingressAccessFunctions = filterIncomingAfs([...filteredNodes, ...filteredCcpags], mapData.accessFunctions);

  return {
    ...mapData,
    accessFunctions: filteredAFs,
    collectionFunctions: filteredCFs,
    xcipioNodes: filteredNodes,
    ccpags: filteredCcpags,
  }
}

const flatPoiList = (poiList: any[]) => {
  const clonedData = cloneDeep(poiList);
  const map = new Map(clonedData.map((data) => [data.data.id, data]));

  return Array.from(map.values());
}

const getConnectedPoiMap = (poiData: any[], poiKey: string) => {
  if (!poiData?.length) {
    return {};
  }

  return poiData
    .map((item: any) => item.connections?.[poiKey] || [])
    .reduce((total: any, item: any) => [...total, ...item], [])
    .reduce((total: any, item: any) => ({ ...total, [item.destinationId]: true }), {});
};

const getXcipioNodesFromConnections = (poiData: any[], xcipioNodes: any[]): any[] => {
  const clonedData = cloneDeep(poiData);
  const xipioNodesFromConnectionsMap = getConnectedPoiMap(clonedData, 'xcipio') as any;
  return cloneDeep(xcipioNodes).filter((node) => xipioNodesFromConnectionsMap?.[node.data.id])
}

const getCollectionFunctionsFromConnections = (filteredPoiData: any[], poiDataToFilterIfConnected: any[]): any[] => {
  const clonedData = cloneDeep(filteredPoiData);
  const connectedPoisMap = getConnectedPoiMap(clonedData, 'cf') as any;
  return cloneDeep(poiDataToFilterIfConnected).filter((item) => connectedPoisMap?.[item.data.id])
}

const getAfsCfsCcpagsFromXcipioNodes = (
  xcipioNodes: any[],
  afs: any[],
  cfs: any,
  ccpags: any[]
): { afs: any[], cfs: any[], ccpags: any[]} => {

  const afsFromConnectionMap = getConnectedPoiMap(xcipioNodes, 'af') as any;
  const cfsFromConnectionMap = getConnectedPoiMap(xcipioNodes, 'cf') as any;
  const ccpagsFromConnectionMap = getConnectedPoiMap(xcipioNodes, 'ccpag') as any;

  return {
    afs: cloneDeep(afs).filter((item: any) => afsFromConnectionMap?.[item.data.id]),
    cfs: cloneDeep(cfs).filter((item: any) => cfsFromConnectionMap?.[item.data.id]),
    ccpags: cloneDeep(ccpags).filter((item: any) => ccpagsFromConnectionMap?.[item.data.id]),
  }
}

const filterByPoiType = (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter,
  availableAccessFunction: SupportedFeaturesAccessFunctions,
  availableCollectionFunction: SupportedFeaturesCollectionFunctions,
):NetworkTopologyMapData => {

  const accessFunctions = cloneDeep(mapData).accessFunctions.filter((af) => isValueIncluded(availableAccessFunction?.[af.data.tag]?.type || '', filter.poiType));
  const ccpags = getXcipioNodesFromConnections(accessFunctions, mapData.ccpags);
  const xcipioNodes = getXcipioNodesFromConnections([...accessFunctions, ...ccpags], mapData.xcipioNodes);
  const collectionFunctions = getCollectionFunctionsFromConnections(xcipioNodes, mapData.collectionFunctions)

  const xcipioNodesWithAfsConnected = getXcipioNodesWhereAfsAreConnected(mapData.xcipioNodes, accessFunctions)

  return {
    ...mapData,
    accessFunctions,
    xcipioNodes: flatPoiList([...xcipioNodes, ...xcipioNodesWithAfsConnected]),
    collectionFunctions,
    ccpags,
  }
}

const filterByVendors = (
  mapData: NetworkTopologyMapData,
  filter: NetworkTopologyFilter,
  availableAccessFunction: SupportedFeaturesAccessFunctions,
):NetworkTopologyMapData => {
  const accessFunctions = cloneDeep(mapData).accessFunctions.filter((af) => isValueIncluded(availableAccessFunction?.[af.data.tag]?.vendor || '', filter.poiVendor));
  const ccpags = getXcipioNodesFromConnections(accessFunctions, mapData.ccpags);
  const xcipioNodes = getXcipioNodesFromConnections([...accessFunctions, ...ccpags], mapData.xcipioNodes);
  const collectionFunctions = getCollectionFunctionsFromConnections(xcipioNodes, mapData.collectionFunctions)

  const xcipioNodesWithAfsConnected = getXcipioNodesWhereAfsAreConnected(mapData.xcipioNodes, accessFunctions)

  return {
    ...mapData,
    accessFunctions,
    collectionFunctions,
    xcipioNodes: flatPoiList([...xcipioNodes, ...xcipioNodesWithAfsConnected]),
    ccpags,
  }
}

const filterPoiBySearch = (
  searchTxt: string,
  availableAccessFunction?: SupportedFeaturesAccessFunctions,
) => {
  return (poi: any) => {
    const poiId = Buffer.from(poi.data.id, 'base64').toString('binary');
    const poiType = getPoiTypeFromOntology(poi, availableAccessFunction);
    const poiVendor = getPoiVendorFromOntology(poi, availableAccessFunction);
    return (
      isSearchIncludedIn(poi.data.city, searchTxt) ||
      isSearchIncludedIn(poi.data.name, searchTxt) ||
      isSearchIncludedIn(poi.data.type, searchTxt) ||
      isSearchIncludedIn(poi.data.nodeName, searchTxt) ||
      isSearchIncludedIn(poi.data.nodeType, searchTxt) ||
      isSearchIncludedIn(poiType, searchTxt) ||
      isSearchIncludedIn(poiVendor, searchTxt) ||
      isSearchIncludedIn(poiId, searchTxt)
    );
  }
}

const getPoiTypeFromOntology = (
  poi: any,
  availableAccessFunction?: SupportedFeaturesAccessFunctions
): string => {
  if (availableAccessFunction == null) {
    return '';
  }

  return (availableAccessFunction[poi.data.tag]?.type || '');
}

const getPoiVendorFromOntology = (
  poi: any,
  availableAccessFunction?: SupportedFeaturesAccessFunctions
): string => {
  if (availableAccessFunction == null) {
    return '';
  }

  return (availableAccessFunction[poi.data.tag]?.vendor || '');
}

const isSearchIncludedIn = (parameter: string, search = '') => {
  if (parameter == null) {
    return false
  }
  return parameter.toLocaleLowerCase().includes(search.toLocaleLowerCase());
}

const setXcipioNodeConnections = (
  filteredNodes: any[],
  filteredAFs: any[],
  filteredCFs: any[],
) => {
  const afMap = filteredAFs.reduce((total, af) => ({ ...total, [af.data.id]: true }), {});
  const cfMap = filteredCFs.reduce((total, cf) => ({ ...total, [cf.data.id]: true }), {});
  return filteredNodes.map((node) => {
    return {
      ...node,
      connections: {
        ...node.connections,
        af: node.connections.af?.filter((af: any) => afMap[af?.destinationId]) || [],
        cf: node.connections.cf?.filter((cf: any) => cfMap[cf?.destinationId]) || [],
      }
    }
  });
}

const getXcipioNodesByAfCf = (
  xcipioNodes: any[],
  poiList: any[]
): any[] => {
  return cloneDeep(xcipioNodes).filter((node) => {
    return poiList.some((poi) => {
      return (poi.connections?.xcipio || []).some((connection: Connection) => connection.destinationId === node.data.id)
    });
  });
}

const getXcipioNodesWhereAfsAreConnected = (
  xcipioNodes: any[],
  poiList: any[]
): any[] => {
  const afMap = poiList.reduce((total, af) => ({ ...total, [af.data.id]: true }), {});
  return cloneDeep(xcipioNodes).filter((node) => {
    return node.connections.af.some((af: any) => afMap[af.destinationId]);
  });
}

const getXcipioNodesByCf = (
  xcipioNodes: any[],
  poiList: any[]
): any[] => {
  const filteredNodes = xcipioNodes.filter((node) => {
    return poiList.some((poi) =>
      (node.connections?.cf || []).some((connection: Connection) => connection.destinationId === poi.data.id)
    );
  });

  return setXcipioNodeConnections(filteredNodes, [], poiList);
}

export const getAfsBasedOnNetworkGeneration = (
  accessFunctions: AccessFunction[],
  supportedAccessFunctions: {
    [key: string]: any;
  },
  enabledFeatures: string[],
  configuredAccessFunction: any[],
  allGenerationSupported?: boolean
): AccessFunction[] => {
  if (allGenerationSupported) {
    return cloneDeep(accessFunctions);
  }

  // Find out enabledAccessFunctions if supportedAccessFunctions features contains any of the enabled features
  const enabledAccessFunctions = getEnabledAccessFunctions(supportedAccessFunctions, enabledFeatures);
  // Filter the enabledAccessFunctions further using configuredAfs
  const configuredAccessFunctions = (JSON.parse(configuredAccessFunction?.[0] ?? '[]')) as ConfiguredAccessFunction[];
  const enabledPoiAccessFunctions = getEnabledPoiAccessFunctions(configuredAccessFunctions, enabledAccessFunctions);
  // Filter the accessFunction which only contains tags of enabledPoiAccessFunctions
  const filteredAccessFunctions = getFilteredAccessFunctions(accessFunctions, enabledPoiAccessFunctions);
  return cloneDeep(filteredAccessFunctions)
    .filter((af) => {
      const services = supportedAccessFunctions?.[af.tag]?.services;

      if (!services || af.tag === 'DRVIRTUAL') {
        return false;
      }

      return true;
    });
}
